/**
 * The station screen's tab structure (core-loop plan 2026-10-25 §4, card CL-5): which tabs sit on the bar and which
 * wait in the grouped "More" drawer, with a clerk's reason line under each tab that isn't usable yet.
 *
 * - The rules live in `src/ui/station-tabs.json` (Zod-validated once at import): each tab id (the tab-bar test id
 *   without `tab-`: `market`, `ext-charts`, ...) maps to a **group**, the **promote** rule that puts it on the bar, and
 *   an optional **reason** line.
 * - `groupTabs` reads existing state only: the lease (owned or not), the hangar (a ship besides the one flown), the
 *   berth store at this station, and the Reach's signals (chart data this station buys, fines or bounties, a
 *   corporation). Without the Reach (`reach` null) those three rules stay off.
 * - Every tab the view lists appears exactly once, on the bar or in More: nothing is hidden or renamed. A tab the json
 *   doesn't list goes to `defaultGroup` and never promotes.
 * - A tab is **usable** when it is on the bar or has no reason line (reference tabs such as Station, Wire, Saves). A
 *   promoted tab carries no reason.
 *
 * Pure. CL-6 renders the result (`screens/Station.tsx`): the bar, then a More button over the grouped drawer; a tab
 * promoted to the bar during the session carries a "new" dot until opened (`trackPromotions`, session-only state).
 */
import { z } from 'zod'
import type { Career } from '../../career/types'
import type { StationTab } from '../contracts'
import { UI_CONFIG, type UiConfig } from '../config'
import stationTabsJson from '../station-tabs.json'

export const PROMOTE_RULES = ['always', 'never', 'leased', 'leaseOwned', 'berthStored', 'chartData', 'finesOrBounties', 'corporation', 'otherShip'] as const
export type PromoteRule = (typeof PROMOTE_RULES)[number]

const text = z.string().min(1)

export const stationTabsFileSchema = z
  .strictObject({
    comment: z.string().optional(),
    groups: z.array(z.strictObject({ id: text, title: text })).min(1),
    defaultGroup: text,
    /** CL-6: the bar's More button and the drawer's strings. */
    labels: z.strictObject({ more: text, moreArrow: text, drawerTitle: text, newDot: text, wireUnread: text, closeHint: text }),
    tabs: z.array(z.strictObject({ id: text, group: text, promote: z.enum(PROMOTE_RULES), reason: text.optional() })),
  })
  .superRefine((f, ctx) => {
    const groups = new Set<string>()
    f.groups.forEach((g, i) => {
      if (groups.has(g.id)) ctx.addIssue({ code: 'custom', path: ['groups', i, 'id'], message: `duplicate group "${g.id}"` })
      groups.add(g.id)
    })
    if (!groups.has(f.defaultGroup)) ctx.addIssue({ code: 'custom', path: ['defaultGroup'], message: `unknown group "${f.defaultGroup}"` })
    const ids = new Set<string>()
    f.tabs.forEach((t, i) => {
      if (ids.has(t.id)) ctx.addIssue({ code: 'custom', path: ['tabs', i, 'id'], message: `duplicate tab "${t.id}"` })
      ids.add(t.id)
      if (!groups.has(t.group)) ctx.addIssue({ code: 'custom', path: ['tabs', i, 'group'], message: `unknown group "${t.group}"` })
      if (t.reason !== undefined && (t.promote === 'always' || t.promote === 'never')) {
        ctx.addIssue({ code: 'custom', path: ['tabs', i, 'reason'], message: `a reason line needs a conditional rule, not "${t.promote}"` })
      }
    })
  })

export type StationTabsConfig = z.infer<typeof stationTabsFileSchema>
export type StationTabRule = StationTabsConfig['tabs'][number]

export const STATION_TABS_CONFIG: StationTabsConfig = stationTabsFileSchema.parse(stationTabsJson)

/** The Reach's state the rules read (CL-6 wires it); null without the Reach. */
export interface TabReachSignals {
  /** The pilot carries chart data this station buys. */
  chartData: boolean
  /** A fine to pay or a bounty to claim. */
  finesOrBounties: boolean
  /** The pilot has founded a corporation. */
  corporation: boolean
  /** CL-6: notable Wire items not yet read (the More button's count); absent = 0. */
  wireUnread?: number
}

/** What `groupTabs` reads from the station view: M2's tabs, the external tabs, and the docked station. */
export interface TabsViewInput {
  readonly station: { readonly id: string }
  readonly tabs: readonly StationTab[]
  readonly external: readonly { readonly id: string; readonly title: string }[]
}

/** What `groupTabs` reads from the career. */
export type TabsCareerInput = Pick<Career, 'lease' | 'hangar' | 'flownShip' | 'storage'>

export interface TabEntry {
  /** The tab-bar test id without `tab-` (`market`, `ext-charts`). */
  id: string
  title: string
  usable: boolean
  /** The clerk's line while the tab isn't usable yet, else null. */
  reason: string | null
}

export interface TabGroup {
  group: string
  title: string
  tabs: TabEntry[]
}

export interface TabGroups {
  /** The bar, in json order. */
  primary: TabEntry[]
  /** The More drawer: non-empty groups in json order. */
  more: TabGroup[]
}

/** The id an external tab's json entry and test id use. */
export function externalTabId(panelId: string): string {
  return `ext-${panelId}`
}

/** Something is stored in the berth at `stationId` (goods, wares or components). */
export function berthHasItems(career: Pick<Career, 'storage'>, stationId: string): boolean {
  const e = career.storage?.[stationId]
  if (e === undefined) return false
  if (e.components.length > 0) return true
  if (Object.values(e.goods).some((q) => q !== undefined && q > 0)) return true
  return Object.values(e.wares).some((q) => q !== undefined && q > 0)
}

/** The pilot owns a ship besides the one flown (a hull a captain could fly, or one stored in the hangar). */
export function ownsOtherShip(career: Pick<Career, 'hangar' | 'flownShip'>): boolean {
  return (career.hangar ?? []).some((s) => s.fleetId !== career.flownShip)
}

/** Whether `rule` puts its tab on the bar now. */
export function promotes(rule: PromoteRule, career: TabsCareerInput, stationId: string, reach: TabReachSignals | null): boolean {
  switch (rule) {
    case 'always':
      return true
    case 'never':
      return false
    case 'leased':
      return !career.lease.owned
    case 'leaseOwned':
      return career.lease.owned
    case 'berthStored':
      return berthHasItems(career, stationId)
    case 'otherShip':
      return ownsOtherShip(career)
    case 'chartData':
      return reach?.chartData ?? false
    case 'finesOrBounties':
      return reach?.finesOrBounties ?? false
    case 'corporation':
      return reach?.corporation ?? false
  }
}

/**
 * The bar and the More drawer for the station `view`: every listed tab once, the promoted ones on the bar in json
 * order, the rest under their groups with their reason lines.
 */
export function groupTabs(
  view: TabsViewInput,
  career: TabsCareerInput,
  reach: TabReachSignals | null,
  cfg: StationTabsConfig = STATION_TABS_CONFIG,
  ui: Pick<UiConfig, 'tabs'> = UI_CONFIG,
): TabGroups {
  const listed: { id: string; title: string }[] = view.tabs.map((t) => ({ id: t, title: ui.tabs[t] }))
  for (const t of view.external) listed.push({ id: externalTabId(t.id), title: t.title })
  const rank = (id: string): number => {
    const i = cfg.tabs.findIndex((r) => r.id === id)
    return i < 0 ? cfg.tabs.length : i
  }
  // Json order first; unlisted tabs keep their view order after the listed ones (a stable sort).
  const ordered = listed.map((t, i) => ({ t, i })).sort((a, b) => rank(a.t.id) - rank(b.t.id) || a.i - b.i)
  const primary: TabEntry[] = []
  const byGroup = new Map<string, TabEntry[]>()
  for (const { t } of ordered) {
    const rule = cfg.tabs.find((r) => r.id === t.id)
    const promoted = rule !== undefined && promotes(rule.promote, career, view.station.id, reach)
    if (promoted) {
      primary.push({ id: t.id, title: t.title, usable: true, reason: null })
      continue
    }
    const reason = rule?.reason ?? null
    const group = rule?.group ?? cfg.defaultGroup
    const list = byGroup.get(group) ?? []
    list.push({ id: t.id, title: t.title, usable: reason === null, reason })
    byGroup.set(group, list)
  }
  const more: TabGroup[] = []
  for (const g of cfg.groups) {
    const tabs = byGroup.get(g.id)
    if (tabs !== undefined && tabs.length > 0) more.push({ group: g.id, title: g.title, tabs })
  }
  return { primary, more }
}

/** CL-6: the More button's slot on the bar (after the primary tabs), or -1 when nothing waits in More. */
export function moreSlot(g: TabGroups): number {
  return g.more.length === 0 ? -1 : g.primary.length
}

/** CL-6: the bar's focus count: the primary tabs plus the More button when anything waits in More. */
export function barCount(g: TabGroups): number {
  return g.primary.length + (g.more.length === 0 ? 0 : 1)
}

/** CL-6: the drawer's tabs in drawer order (groups in order, tabs in order): the `G.more` focus indices. */
export function moreTabs(g: TabGroups): TabEntry[] {
  const out: TabEntry[] = []
  for (const grp of g.more) for (const t of grp.tabs) out.push(t)
  return out
}

/** CL-6: where the showing tab `activeId` sits on the bar: its index, or the More slot when it is in the drawer. */
export function barIndexOf(g: TabGroups, activeId: string): number {
  const i = g.primary.findIndex((t) => t.id === activeId)
  return i >= 0 ? i : moreSlot(g)
}

/** CL-6: the session's "new" dots: tabs seen in More, and promoted tabs not yet opened. Session-only (Q9: no save change). */
export interface TabDots {
  readonly seenInMore: ReadonlySet<string>
  readonly fresh: ReadonlySet<string>
}

export const NO_DOTS: TabDots = Object.freeze({ seenInMore: new Set<string>(), fresh: new Set<string>() })

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const x of a) if (!b.has(x)) return false
  return true
}

/**
 * CL-6: the dots after the station shows `g` with `activeId` open. A tab seen in More this session that is now on the
 * bar gets a dot; opening a tab clears its dot; a tab that drops back to More can earn one again. Returns `prev`
 * itself when nothing changed. Pure.
 */
export function trackPromotions(prev: TabDots, g: TabGroups, activeId: string): TabDots {
  const seen = new Set(prev.seenInMore)
  const fresh = new Set(prev.fresh)
  for (const t of g.primary) {
    if (seen.has(t.id)) {
      seen.delete(t.id)
      fresh.add(t.id)
    }
  }
  for (const grp of g.more) {
    for (const t of grp.tabs) {
      seen.add(t.id)
      fresh.delete(t.id)
    }
  }
  fresh.delete(activeId)
  if (sameSet(seen, prev.seenInMore) && sameSet(fresh, prev.fresh)) return prev
  return { seenInMore: seen, fresh }
}
